import { Router } from '@angular/router';
import { AuthService } from './../auth/auth.service';
import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { LoginService } from './login.service';
import { MatDialog } from '@angular/material/dialog';
import { ForgotPasswordComponent } from '../components/forgot-password/forgot-password.component';
import { GoogleAuthService } from '../shared/services/google-auth.service';

import {
    ACCESS_KEYS,
    LocalStorageService,
} from '../shared/services/local-storage.service';
/** Error when invalid control is dirty, touched, or submitted. */
@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('googleBtn') googleBtn!: ElementRef<HTMLElement>;
    isloading: boolean = false;
    opacityLevel: any;
    messageAlert: any;
    newUserCheck: boolean = false;
    bpAlert: boolean = false;
    hidePassword: boolean = true;
    googleUnavailable: boolean = false;
    currentYear = new Date().getFullYear();
    loginFormControl = new FormControl('', [
        Validators.required,
        Validators.email,
    ]);
    pressroomId: any;
    durationInSeconds = 5;
    private credentialSub?: Subscription;
    constructor(
        public auth: AuthService,
        public route: Router,
        private loginService: LoginService,
        private dialog: MatDialog,
        public http: HttpClient,
        private localStorageService: LocalStorageService,
        private googleAuth: GoogleAuthService,
    ) {}

    ngOnInit(): void {
        // Subscribe once; the button render (below) is what triggers emissions.
        this.credentialSub = this.googleAuth
            .onCredential()
            .subscribe((credential: string) =>
                this.handleGoogleCredential(credential),
            );
    }

    ngAfterViewInit(): void {
        this.googleAuth
            .renderButton(this.googleBtn.nativeElement)
            .then((rendered: boolean) => {
                this.googleUnavailable = !rendered;
            });
    }

    ngOnDestroy(): void {
        this.credentialSub?.unsubscribe();
    }

    private handleGoogleCredential(credential: string): void {
        this.isloading = true;
        this.opacityLevel = 0.5;
        this.bpAlert = false;
        this.loginService.googleSignIn(credential).subscribe(
            (response: any) => {
                if (response?.type === 'user') {
                    // Identical post-login flow to password sign-in.
                    if (this.auth.isAuthenticated()) {
                        if (this.auth.isPasswordUpdated()) {
                            this.loginService
                                .updateAccountList()
                                .then((value: any) => {
                                    this.setCurrentPressroom(value);
                                    this.route.navigate(['/dashboard']);
                                });
                        } else {
                            this.route.navigate(['/settings/user-account']);
                        }
                    }
                } else if (response?.type === 'lead') {
                    // Leads have no token — route to onboarding with the verified credential
                    // and status (kept in navigation state, never in the URL).
                    this.route.navigate(['/onboarding'], {
                        state: {
                            credential,
                            lead: response.lead,
                            status: response.status,
                        },
                    });
                }
                this.isloading = false;
                this.opacityLevel = 1;
            },
            (err: any) => {
                this.messageAlert =
                    err?.error?.message ||
                    'Google sign-in failed. Please try again.';
                this.bpAlert = true;
                this.isloading = false;
                this.opacityLevel = 1;
            },
        );
    }

    login(userEmail: string, userPassword: string) {
        this.isloading = true;
        this.opacityLevel = 0.5;
        this.loginService.loginUser(userEmail, userPassword).subscribe(
            (val: any) => {
                if (this.auth.isAuthenticated()) {
                    if (this.auth.isPasswordUpdated()) {
                        this.loginService
                            .updateAccountList()
                            .then((value: any) => {
                                this.setCurrentPressroom(value);
                                this.route.navigate(['/dashboard']);
                            });
                    } else {
                        this.route.navigate(['/settings/user-account']);
                    }
                    this.isloading = false;
                    this.opacityLevel = 1;
                } else {
                    this.messageAlert = 'Username or password are not valid';
                    this.bpAlert = true;
                    this.opacityLevel = 1;
                    this.isloading = false;
                }
            },
            (err: any) => {
                if (err.error.response) {
                    this.messageAlert = err.error.response;
                } else {
                    this.messageAlert = 'Username or password are not valid';
                }
                this.bpAlert = true;
                this.opacityLevel = 1;
                this.isloading = false;
            },
        );
    }

    setCurrentPressroom(value: string | any[]) {
        if (
            this.localStorageService
                .getItem(ACCESS_KEYS.CURRENT_PRESSROOM)
                ?.toString() === 'noPressroom'
        ) {
            if (value.length > 0) {
                if (value[0].pressrooms.length > 0) {
                    this.pressroomId = value[0].pressrooms[0].id;
                    this.localStorageService.setItemSafe(
                        ACCESS_KEYS.CURRENT_PRESSROOM,
                        value[0].pressrooms[0].id,
                    );
                    this.localStorageService.setItemSafe(
                        ACCESS_KEYS.CURRENT_PRESSROOM_PATH,
                        value[0].pressrooms[0].path,
                    );
                } else {
                    this.localStorageService.setItem(
                        ACCESS_KEYS.CURRENT_PRESSROOM,
                        'noPressroom',
                    );
                }
            } else {
                this.localStorageService.setItem(
                    ACCESS_KEYS.CURRENT_PRESSROOM,
                    'noPressroom',
                );
            }
        }
    }
    forgotPassword() {
        const forgotPassword = this.dialog.open(ForgotPasswordComponent);
        forgotPassword.afterClosed().subscribe((result) => {});
    }
}
